import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppUser, Task, TaskStatus, Workstream } from '../../pm/types';
import {
  DEFAULT_WORKSTREAM_COLOR,
  STATUS_LABELS,
  STATUS_ORDER,
} from '../../pm/constants';
import { assignTaskOwner, updateTask } from '../../pm/firebase';
import { linkify } from '../../pm/linkify';
import { taskShareUrl, copyToClipboard } from '../../pm/share';
import StatusPicker from './StatusPicker';
import OwnerPicker from './OwnerPicker';
import DueDatePicker from './DueDatePicker';

interface Props {
  tasks: Task[];                  // pre-filtered task set (owner / status / etc.)
  workstreams: Workstream[];      // for the colored workstream stripe on each card
  admins: AppUser[];
  currentUid: string;
  projectId: string;
  highlightedTaskId?: string | null;
}

const KanbanView: React.FC<Props> = ({
  tasks, workstreams, admins, currentUid, projectId, highlightedTaskId,
}) => {
  const history = useHistory();
  // Drag state for card-to-column drops. We only need to know which task is
  // being dragged and which column the cursor is currently over so we can
  // style both. The actual status change happens on drop.
  const [dragTaskId, setDragTaskId] = useState<string | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<TaskStatus | null>(null);

  const wsById = useMemo(() => {
    const m: Record<string, Workstream> = {};
    for (const w of workstreams) m[w.id] = w;
    return m;
  }, [workstreams]);

  // Group tasks by status. Within a column we keep done sorted by recency
  // (matches the list view's auto-bottom-for-done convention) and the rest
  // by stored sortOrder.
  const tasksByStatus = useMemo(() => {
    const m: Record<TaskStatus, Task[]> = { todo: [], progress: [], blocked: [], done: [] };
    for (const t of tasks) m[t.status].push(t);
    for (const s of STATUS_ORDER) {
      m[s].sort((a, b) => {
        if (s === 'done') return (b.updatedAt || 0) - (a.updatedAt || 0);
        return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      });
    }
    return m;
  }, [tasks]);

  async function changeStatus(task: Task, s: TaskStatus) {
    await updateTask(task.id, { status: s }, currentUid);
  }
  async function changeOwner(task: Task, uid: string) {
    await assignTaskOwner(task.id, task.projectId, uid, task.ownerId || '', task.label, currentUid);
  }
  async function changeDue(task: Task, ms: number | null) {
    await updateTask(task.id, { dueDate: ms }, currentUid);
  }

  async function copyLink(task: Task) {
    const url = taskShareUrl(task.projectId, task.id);
    const ok = await copyToClipboard(url);
    if (!ok) window.prompt('Copy this task link:', url);
  }

  function openInListView(task: Task) {
    // Hash-only nav lands the user back in the project (list view) and the
    // existing deep-link wiring scrolls + highlights the task.
    history.replace({
      pathname: `/Projects/${projectId}`,
      hash: `#task-${task.id}`,
    });
  }

  // Drop a card into a column → set the task's status to that column.
  function handleDropOnColumn(status: TaskStatus) {
    if (!dragTaskId) return;
    const t = tasks.find((x) => x.id === dragTaskId);
    setDragTaskId(null);
    setDragOverStatus(null);
    if (!t || t.status === status) return; // no-op when dropped on the same column
    updateTask(t.id, { status }, currentUid);
  }

  return (
    <div className="pm-kanban">
      {STATUS_ORDER.map((s) => {
        const col = tasksByStatus[s];
        const isDropTarget = dragOverStatus === s;
        const isDropTargetSelf = isDropTarget && dragTaskId
          ? tasks.find((t) => t.id === dragTaskId)?.status === s
          : false;
        return (
          <div
            key={s}
            className={[
              'pm-kanban-col',
              `pm-kanban-col-${s}`,
              isDropTarget ? 'pm-kanban-col-droptarget' : '',
              isDropTargetSelf ? 'pm-kanban-col-droptarget-self' : '',
            ].filter(Boolean).join(' ')}
            onDragOver={(e) => {
              if (!dragTaskId) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (dragOverStatus !== s) setDragOverStatus(s);
            }}
            onDragLeave={(e) => {
              // Only clear if the cursor truly left the column (not just moved
              // to a child element). relatedTarget can be null when dragging
              // outside the window.
              const next = e.relatedTarget as Node | null;
              if (next && e.currentTarget.contains(next)) return;
              if (dragOverStatus === s) setDragOverStatus(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              handleDropOnColumn(s);
            }}
          >
            <div className="pm-kanban-col-header">
              <span className="pm-kanban-col-title">{STATUS_LABELS[s]}</span>
              <span className="pm-kanban-col-count">{col.length}</span>
            </div>
            <div className="pm-kanban-col-body">
              {col.length === 0 ? (
                <div className="pm-kanban-empty">No tasks</div>
              ) : col.map((t) => {
                const ws = wsById[t.workstreamId];
                const wsColor = ws?.color || DEFAULT_WORKSTREAM_COLOR;
                const highlighted = highlightedTaskId === t.id;
                const isDragging = dragTaskId === t.id;
                return (
                  <div
                    key={t.id}
                    className={[
                      'pm-kanban-card',
                      highlighted ? 'pm-task-highlighted' : '',
                      isDragging ? 'pm-kanban-card-dragging' : '',
                    ].filter(Boolean).join(' ')}
                    style={{ borderLeftColor: wsColor }}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', t.id);
                      setDragTaskId(t.id);
                    }}
                    onDragEnd={() => {
                      setDragTaskId(null);
                      setDragOverStatus(null);
                    }}
                  >
                    <div className="pm-kanban-card-ws" title={ws?.title || ''}>
                      {ws?.title || 'No workstream'}
                    </div>
                    <button
                      type="button"
                      className="pm-kanban-card-title"
                      onClick={() => openInListView(t)}
                      title="Open in list view"
                    >
                      {linkify(t.label, { stopPropagation: true })}
                    </button>
                    {t.description ? (
                      <div className="pm-kanban-card-desc">
                        {linkify(t.description, { stopPropagation: true })}
                      </div>
                    ) : null}
                    {/* Interactive rows: stopping mousedown propagation keeps
                        a click on a pill from initiating the parent card's
                        drag. (Quick clicks usually wouldn't move far enough
                        to trigger a drag, but a slow press would.) */}
                    <div className="pm-kanban-card-meta" onMouseDown={(e) => e.stopPropagation()}>
                      <StatusPicker status={t.status} onChange={(ns) => changeStatus(t, ns)} />
                      <OwnerPicker ownerId={t.ownerId || ''} admins={admins} onChange={(uid) => changeOwner(t, uid)} />
                    </div>
                    <div className="pm-kanban-card-foot" onMouseDown={(e) => e.stopPropagation()}>
                      <DueDatePicker
                        dueDate={t.dueDate}
                        taskDone={t.status === 'done'}
                        onChange={(ms) => changeDue(t, ms)}
                      />
                      <button
                        type="button"
                        className="pm-link-button pm-kanban-copy-link"
                        onClick={() => copyLink(t)}
                        title="Copy a shareable link that opens this task"
                      >
                        Copy link
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KanbanView;
